import { useState, useLayoutEffect, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { usePlaceContext } from '../PlaceContext';
import { colors } from '../../common/ColorPicker';
import useApi from '../../../Api';
import debounce from 'lodash/debounce';
import { useAuth } from '../../../AuthContext';
import { toast } from 'react-toastify';

const useColumns = (initialColumns, saveItineraryColumns, readOnly = false) => {
  const [columns, setColumns] = useState(initialColumns);
  const [columnsToSave, setColumnsToSave] = useState(null);
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const { stateRef, setColumnMarkerColor, setColumnVisibility, deleteColumnAndUpdateMarkers } = usePlaceContext();
  const apiClient = useApi();
  const { token } = useAuth();

  useLayoutEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  useEffect(() => {
    if (columnsToSave) {
      saveItineraryColumns(columnsToSave);
      setColumnsToSave(null);
    }
  }, [columnsToSave, saveItineraryColumns]);

  useEffect(() => {
    if (editingColumnIndex !== null) {
      const input = document.getElementById(`column-input-${editingColumnIndex}`);
      if (input) {
        input.focus();
      }
      setEditingColumnIndex(null);
    }
  }, [editingColumnIndex]);

  const transformPlaceData = (place) => ({
    ...place,
    photoUrl:
      place.photoUrl ||
      (place.photos && place.photos.length > 0
        ? place.photos[0].url
        : null),
    about: place.about || '',
    userRatingsTotal: place.userRatingsTotal || 0,
    formattedAddress: place.formattedAddress || `${place.city}, ${place.country}`,
    photos: place.photos || [],
    openingHours: place.openingHours || [],
    website: place.website || '',
    priceLevel: place.priceLevel || 0,
    businessStatus: place.businessStatus || 'OPERATIONAL',
    adrAddress: place.adrAddress || '',
    plusCode: place.plusCode || '',
  });

  const getNextDayName = (cols) => `Day ${cols.length + 1}`;

  const addColumn = useCallback(
    debounce(async () => {
      if (readOnly) return;
      if (columns.length >= 10) {
        toast.error('The maximum number of columns per itinerary is 10.', {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }
      const nextColumnName = getNextDayName(columns);
      const usedColors = columns.map((col) => {
        const colColor = stateRef.current.selectedItinerary.columnMarkerColors[col.id];
        return colColor || '#FFFFFF';
      });
      const savedPlacesColor = stateRef.current.selectedItinerary.columnMarkerColors['saved-places'];
      if (savedPlacesColor) {
        usedColors.push(savedPlacesColor);
      }
      const usedColorsSet = new Set(usedColors);
      const availableColors = colors.filter((c) => !usedColorsSet.has(c));
      const finalColor = availableColors.length > 0 ? availableColors[0] : colors[0];

      const newColumnId = uuidv4();
      const newColumn = {
        title: nextColumnName,
        items: [],
        id: newColumnId,
        isEditing: false,
      };

      const newColumns = [...columns, newColumn];
      setColumns(newColumns);
      setColumnsToSave(newColumns);

      setColumnMarkerColor(newColumnId, finalColor);
      setColumnVisibility(newColumnId, true);

      const itineraryId = stateRef.current.selectedItinerary.id;
      try {
        await apiClient.post(
          `/itineraries/${itineraryId}/columns`,
          {
            column_id: newColumnId,
            name: nextColumnName,
            position: newColumns.length - 1,
            visibility: true,
            color: finalColor,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Error adding column:', error);
      }
    }, 100),
    [columns, stateRef, setColumnMarkerColor, setColumnVisibility, apiClient, token, readOnly]
  );

  const deleteColumn = useCallback(
    debounce(async (columnId) => {
      if (readOnly) return;
      const itineraryId = stateRef.current.selectedItinerary.id;
      setColumns((prevColumns) => {
        const newColumns = deleteColumnAndUpdateMarkers(columnId, prevColumns);
        setColumnsToSave(newColumns);
        return newColumns;
      });
      try {
        await apiClient.delete(`/columns/${columnId}?itinerary_id=${itineraryId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error deleting column:', error);
      }
    }, 100),
    [deleteColumnAndUpdateMarkers, stateRef, apiClient, token, readOnly]
  );

  const addPlaceToColumn = useCallback(
    (columnId, place) => {
      if (readOnly) return;
      setColumns((prevColumns) => {
        const updatedColumns = prevColumns.map((column) =>
          column.id === columnId ? { ...column, items: [...column.items, transformPlaceData(place)] } : column
        );
        setColumnsToSave(updatedColumns);
        return updatedColumns;
      });
    },
    [setColumns, setColumnsToSave, readOnly]
  );

  const handleColumnTitleChange = useCallback((index, newTitle, columnId) => {
    if (readOnly) return;
    if (newTitle.length <= 25) {
      setColumns(prevColumns => {
        const updated = prevColumns.map((col, i) => i === index ? { ...col, title: newTitle } : col);
        setColumnsToSave(updated);
        return updated;
      });
    }
    const itineraryId = stateRef.current.selectedItinerary.id;
    setTimeout(async () => {
      try {
        await apiClient.patch(`/columns/${columnId}/rename?itinerary_id=${itineraryId}`, { name: newTitle }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error updating column title:', error);
      }
    }, 500);
  }, [setColumns, setColumnsToSave, stateRef, apiClient, token, readOnly]);

  const handleColumnBlur = useCallback((index) => {
    if (readOnly) return;
    setColumns(prev => {
      const updated = prev.map((col, i) => i === index ? { ...col, isEditing: false } : col);
      setColumnsToSave(updated);
      return updated;
    });
  }, [setColumns, setColumnsToSave, readOnly]);

  const handleColumnKeyDown = useCallback((e, index) => {
    if (readOnly) return;
    if (e.key === 'Enter') handleColumnBlur(index);
  }, [handleColumnBlur, readOnly]);

  const startColumnEditing = useCallback((index) => {
    if (readOnly) return;
    setColumns(prev => prev.map((col, i) => i === index ? { ...col, isEditing: true } : col));
    setEditingColumnIndex(index);
  }, [setColumns, setEditingColumnIndex, readOnly]);

  const handleDateChange = useCallback((index, date) => {
    if (readOnly) return;
    setColumns(prev => {
      const updated = prev.map((col, i) => i === index ? { ...col, date } : col);
      setColumnsToSave(updated);
      return updated;
    });
    setTimeout(async () => {
      const itineraryId = stateRef.current.selectedItinerary.id;
      const columnId = columns[index].id;
      try {
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        await apiClient.patch(`/itineraries/${itineraryId}/columns/${columnId}/date/update`, { date: formattedDate }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error updating column date:', error);
      }
    }, 500);
  }, [setColumns, setColumnsToSave, stateRef, apiClient, columns, token, readOnly]);

  const handleNotesChange = useCallback((columnId, placeId, newNotes) => {
    if (readOnly) return;
    setColumns(prev => {
      const updated = prev.map(col => col.id === columnId ? { ...col, items: col.items.map(item => item.id === placeId ? { ...item, notes: newNotes } : item) } : col);
      setColumnsToSave(updated);
      return updated;
    });
  }, [setColumns, setColumnsToSave, readOnly]);

  return {
    columns,
    addColumn,
    deleteColumn,
    addPlaceToColumn,
    setColumnsToSave,
    setEditingColumnIndex,
    setColumns,
  };
};

export default useColumns;
